export const DC_CLIENT = {
  id: "travis-scott",
  name: "Travis Scott",
  avatar:
    "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200&q=70",
  role: "Recording Artist · Cactus Jack",
  context:
    "Existing Air Jordan partnership winding down Q1 2026. Three brands actively competing for the next sneaker deal. Decision needed by Mar 15.",
};

export const DC_DEAL_TYPE = "Athlete / Artist Sneaker Collaboration";
export const DC_DEADLINE = "March 15, 2026";

export interface DealDimension {
  id: keyof DealOfferCell | "cash" | "royalty" | "term" | "exclusivity" | "deliverables" | "territory" | "morals" | "termination" | "approval" | "ip" | "offramp";
  label: string;
  group: string;
  icon: string;
  weight: "high" | "med" | "low";
}

export interface DealCell {
  headline: string;
  sub: string;
  detail: string;
  bonuses?: string;
  equity?: string;
  total?: string;
  renewal?: string;
  flag?: boolean;
  score?: number;
}

export interface DealOffer {
  id: string;
  brand: string;
  division: string;
  submitted: string;
  rep: string;
  summary: string;
  swatchClass: string;
  accentClass: string;
  cash: DealCell;
  royalty: DealCell;
  term: DealCell;
  exclusivity: DealCell;
  deliverables: DealCell;
  territory: DealCell;
  morals: DealCell;
  termination: DealCell;
  approval: DealCell;
  ip: DealCell;
  offramp: DealCell;
}

export type DealOfferCell = Omit<DealOffer, "id" | "brand" | "division" | "submitted" | "rep" | "summary" | "swatchClass" | "accentClass">;

export const DC_DIMENSIONS: DealDimension[] = [
  { id: "cash", label: "Total cash value", group: "$", icon: "dollar", weight: "high" },
  { id: "royalty", label: "Royalty / equity", group: "$", icon: "percent", weight: "high" },
  { id: "term", label: "Term & renewal", group: "time", icon: "calendar", weight: "med" },
  { id: "exclusivity", label: "Exclusivity scope", group: "rights", icon: "shield", weight: "high" },
  { id: "deliverables", label: "Deliverables", group: "work", icon: "list", weight: "med" },
  { id: "territory", label: "Territory", group: "rights", icon: "globe", weight: "low" },
  { id: "morals", label: "Morals clause", group: "rights", icon: "gavel", weight: "high" },
  { id: "termination", label: "Termination triggers", group: "rights", icon: "door", weight: "high" },
  { id: "approval", label: "Approval / creative", group: "work", icon: "pen", weight: "med" },
  { id: "ip", label: "IP & likeness", group: "rights", icon: "fingerprint", weight: "high" },
  { id: "offramp", label: "Off-ramp / out clauses", group: "rights", icon: "exit", weight: "med" },
];

/** Card header, toolbar pill, and card accent per sneaker brand */
export const DC_OFFER_BRAND_STYLES: Record<
  string,
  {
    headerClass: string;
    accentClass: string;
    pillClass: string;
    pillSubtextClass: string;
  }
> = {
  nike: {
    headerClass: "bg-[#111111] text-white",
    accentClass: "bg-[#111111]/[0.06]",
    pillClass:
      "border-0 bg-[#111111] text-white shadow-none hover:bg-[#111111]/90 dark:border-0 dark:bg-[#111111] dark:hover:bg-[#111111]/90",
    pillSubtextClass: "text-white/70",
  },
  adidas: {
    headerClass: "bg-[#000000] text-white",
    accentClass: "bg-black/[0.05]",
    pillClass:
      "border-0 bg-black text-white shadow-none hover:bg-black/90 dark:border-0 dark:bg-black dark:hover:bg-black/90",
    pillSubtextClass: "text-white/70",
  },
  newbalance: {
    headerClass: "bg-[#DA291C] text-white",
    accentClass: "bg-[#DA291C]/[0.08]",
    pillClass:
      "border-0 bg-[#DA291C] text-white shadow-none hover:bg-[#DA291C]/90 dark:border-0 dark:bg-[#DA291C] dark:hover:bg-[#DA291C]/90",
    pillSubtextClass: "text-white/80",
  },
};

export const DC_OFFERS: DealOffer[] = [
  {
    id: "nike",
    brand: "Nike",
    division: "Jordan Brand",
    swatchClass: DC_OFFER_BRAND_STYLES.nike.headerClass,
    accentClass: DC_OFFER_BRAND_STYLES.nike.accentClass,
    submitted: "Feb 28, 2026",
    rep: "Larry Miller, Chairman",
    summary:
      "Continuation play. Lower upfront, highest royalty rate, multi-cycle commitment with Jordan flagship status.",
    cash: {
      headline: "$45M",
      sub: "3-yr guarantee",
      detail: "$12M signing + $11M/yr × 3",
      bonuses: "Performance bonus up to $8M (sell-through > 80%)",
      score: 72,
    },
    royalty: {
      headline: "12%",
      sub: "on net wholesale",
      detail: "Tiered: 12% up to $200M, 14% above",
      equity: "No equity",
      score: 88,
    },
    term: {
      headline: "3 + 2 + 2",
      sub: "7 yrs max",
      detail: "3-yr base, two 2-yr options at Nike election",
      renewal: "Auto-renew unless 180-day notice",
      score: 70,
    },
    exclusivity: {
      headline: "Footwear only",
      sub: "Loose",
      detail:
        "Exclusive in basketball + lifestyle sneakers. Free to do apparel, accessories, other categories.",
      score: 82,
    },
    deliverables: {
      headline: "4 / yr",
      sub: "Drops",
      detail: "4 collab drops/yr · 3 social posts/drop · 2 brand events",
      total: "~24 deliverables/yr",
      score: 78,
    },
    territory: {
      headline: "Worldwide",
      sub: "Full global rights",
      detail: "Global retail + DTC + travel retail",
      score: 95,
    },
    morals: {
      headline: "Mutual",
      sub: "Both ways",
      detail:
        'Either party can invoke. 30-day cure period. Defined "material reputational harm" standard.',
      score: 90,
    },
    termination: {
      headline: "Cause-only",
      sub: "Standard",
      detail: "Material breach + 60-day cure. No convenience termination by either side.",
      score: 85,
    },
    approval: {
      headline: "Joint",
      sub: "Mutual sign-off",
      detail: "Travis has design veto. Marketing approval mutual. Final cuts: brand decides.",
      score: 80,
    },
    ip: {
      headline: "License",
      sub: "Licensed for term",
      detail:
        "Travis retains all likeness rights. Nike licenses for term + 6-mo sell-off. No archival use post-term.",
      score: 88,
    },
    offramp: {
      headline: "180-day notice",
      sub: "Limited",
      detail:
        "Termination for cause only. No convenience exit. Post-term non-compete: 6 months category.",
      flag: true,
      score: 60,
    },
  },
  {
    id: "adidas",
    brand: "Adidas",
    division: "Yeezy Successor / Adidas Originals",
    swatchClass: DC_OFFER_BRAND_STYLES.adidas.headerClass,
    accentClass: DC_OFFER_BRAND_STYLES.adidas.accentClass,
    submitted: "Mar 03, 2026",
    rep: "Bjørn Gulden, CEO",
    summary:
      "Aggressive headline number. Highest guarantee, but tight exclusivity and concerning morals language.",
    cash: {
      headline: "$72M",
      sub: "3-yr guarantee",
      detail: "$25M signing + $15.7M/yr × 3",
      bonuses: "Equity grant: 0.5% Adidas Originals sub-brand · vests over 4 yrs",
      score: 95,
    },
    royalty: {
      headline: "8%",
      sub: "flat",
      detail: "Flat 8% on net wholesale, no tier escalator",
      equity: "0.5% sub-brand equity (≈$8M paper value)",
      score: 75,
    },
    term: {
      headline: "5 + 5",
      sub: "10 yrs max",
      detail: "5-yr base + Adidas-elected 5-yr extension at same terms",
      renewal: "Brand option only — Travis cannot decline extension",
      flag: true,
      score: 55,
    },
    exclusivity: {
      headline: "Whole footwear",
      sub: "Tight",
      detail:
        'Exclusive across all footwear categories AND all athletic apparel. Includes "any wearable product worn on foot or leg."',
      flag: true,
      score: 45,
    },
    deliverables: {
      headline: "6 / yr",
      sub: "Drops + content",
      detail: "6 drops/yr · 5 social posts/drop · 4 brand events · 2 international tours",
      total: "~50 deliverables/yr",
      score: 60,
    },
    territory: {
      headline: "Worldwide",
      sub: "Global, with conditions",
      detail: "Global rights but Adidas controls China distribution exclusively",
      score: 80,
    },
    morals: {
      headline: "One-way",
      sub: "Brand-favor",
      detail:
        'Brand-only invocation. Vague "brand-damaging conduct" language. No mutual provision. No defined cure period.',
      flag: true,
      score: 35,
    },
    termination: {
      headline: "Wide latitude",
      sub: "Brand-favor",
      detail:
        "Brand can terminate for cause OR convenience with 90-day notice. Travis bears 50% of unrecouped advance.",
      flag: true,
      score: 40,
    },
    approval: {
      headline: "Brand-led",
      sub: "Limited veto",
      detail: "Travis gets 2 design veto rounds per drop. Marketing creative: brand final decision.",
      flag: true,
      score: 55,
    },
    ip: {
      headline: "Perpetual",
      sub: "Concerning",
      detail:
        'Adidas gets perpetual archival use of all materials created during term. "Cactus Jack" mark co-owned in footwear category.',
      flag: true,
      score: 30,
    },
    offramp: {
      headline: "No convenience",
      sub: "Locked in",
      detail:
        "Travis cannot terminate for convenience. Cause-only with 120-day cure. Aggressive non-compete: 18 months.",
      flag: true,
      score: 35,
    },
  },
  {
    id: "newbalance",
    brand: "New Balance",
    division: "Made in USA / Lifestyle",
    swatchClass: DC_OFFER_BRAND_STYLES.newbalance.headerClass,
    accentClass: DC_OFFER_BRAND_STYLES.newbalance.accentClass,
    submitted: "Mar 05, 2026",
    rep: "Joe Preston, CEO",
    summary:
      "Most flexible terms, lower upfront. Strongest creative control and shortest exclusivity. Equity sweetener.",
    cash: {
      headline: "$38M",
      sub: "3-yr guarantee",
      detail: "$8M signing + $10M/yr × 3",
      bonuses: "Equity stake: 1% of co-branded sub-line · vests yr 2",
      score: 65,
    },
    royalty: {
      headline: "15%",
      sub: "on net wholesale",
      detail: "15% flat + 2% bonus tier above $150M sales",
      equity: "1% co-branded sub-line equity",
      score: 92,
    },
    term: {
      headline: "2 + 2",
      sub: "4 yrs max",
      detail: "2-yr base, mutual option to renew 2 yrs at re-negotiated terms",
      renewal: "Mutual — both sides can decline",
      score: 92,
    },
    exclusivity: {
      headline: "Lifestyle only",
      sub: "Narrow",
      detail:
        "Exclusive in lifestyle/heritage running only. Free to do basketball, athletic, all apparel.",
      score: 95,
    },
    deliverables: {
      headline: "3 / yr",
      sub: "Drops",
      detail: "3 drops/yr · 2 social posts/drop · 1 brand event",
      total: "~12 deliverables/yr",
      score: 90,
    },
    territory: {
      headline: "Worldwide",
      sub: "Full global, equal",
      detail: "Worldwide retail, DTC, all channels. No regional carve-outs.",
      score: 98,
    },
    morals: {
      headline: "Mutual",
      sub: "Both ways",
      detail:
        'Mutual invocation. 60-day cure. Specific defined standard ("conviction OR public admission").',
      score: 95,
    },
    termination: {
      headline: "Mutual cause",
      sub: "Symmetric",
      detail:
        "Either side: material breach + 90-day cure. Convenience: either side, 12-mo notice.",
      score: 88,
    },
    approval: {
      headline: "Travis-led",
      sub: "Strong veto",
      detail:
        "Travis has full creative veto on design. Mutual sign-off on marketing. Final art: artist decides.",
      score: 95,
    },
    ip: {
      headline: "Travis owns",
      sub: "Clean",
      detail:
        "All Cactus Jack marks and likeness retained. NB licenses for term + 90-day sell-off. Reverts clean.",
      score: 95,
    },
    offramp: {
      headline: "Mutual exit",
      sub: "Flexible",
      detail:
        "Either party may terminate for convenience after yr 1 with 6-mo notice. No non-compete post-term.",
      score: 92,
    },
  },
];

export const DC_DELTA_NOTES: Record<
  string,
  { winner: string; note: string }
> = {
  cash: {
    winner: "adidas",
    note: "Adidas is +$27M over Nike, +$34M over NB — but headline only. Equity components shift the picture.",
  },
  royalty: {
    winner: "newbalance",
    note: "NB highest rate (15% + escalator). Nike tiered. Adidas flat 8% but adds 0.5% sub-brand equity.",
  },
  term: {
    winner: "newbalance",
    note: "NB shortest, mutual options. Adidas locks in up to 10 yrs at brand election — meaningful flexibility cost.",
  },
  exclusivity: {
    winner: "newbalance",
    note: "Adidas exclusivity is unusually broad (footwear + apparel). NB narrowest. Nike middle ground.",
  },
  deliverables: {
    winner: "newbalance",
    note: "Adidas requests 50 deliverables/yr — heavy. NB asks 12. Workload differential is significant.",
  },
  territory: {
    winner: "newbalance",
    note: "All three offer worldwide; Adidas carves out China distribution control.",
  },
  morals: {
    winner: "newbalance",
    note: "Adidas one-way + vague language is the most concerning clause across all three deals.",
  },
  termination: {
    winner: "newbalance",
    note: "Adidas allows brand convenience termination + claws back 50% of unrecouped advance. NB and Nike symmetric.",
  },
  approval: {
    winner: "newbalance",
    note: "NB gives strongest creative control; Adidas is brand-led. Material for Travis specifically.",
  },
  ip: {
    winner: "newbalance",
    note: "Adidas takes perpetual archival rights and co-owns Cactus Jack mark in footwear — major concern.",
  },
  offramp: {
    winner: "newbalance",
    note: "NB allows mutual convenience exit after yr 1. Adidas locks in 18-mo post-term non-compete.",
  },
};

export const DC_COMPOSITE: Record<
  string,
  { financial: number; flexibility: number; rights: number; workload: number; overall: number }
> = {
  nike: { financial: 80, flexibility: 65, rights: 78, workload: 78, overall: 75 },
  adidas: { financial: 85, flexibility: 38, rights: 35, workload: 60, overall: 54 },
  newbalance: { financial: 78, flexibility: 92, rights: 95, workload: 92, overall: 89 },
};

export interface SampleUploadFile {
  id: string;
  offerId: string;
  name: string;
  pages: number;
  size: string;
  from: string;
  received: string;
}

export const DC_SAMPLE_FILES: SampleUploadFile[] = [
  {
    id: "nike",
    offerId: "nike",
    name: "Nike_Jordan_TS_Offer_v3.pdf",
    pages: 47,
    size: "2.1 MB",
    from: "Larry Miller (Nike)",
    received: "Feb 28",
  },
  {
    id: "adidas",
    offerId: "adidas",
    name: "adidas_Yeezy-Successor_TS.pdf",
    pages: 38,
    size: "1.7 MB",
    from: "Brian Grevy (adidas)",
    received: "Mar 02",
  },
  {
    id: "newbalance",
    offerId: "newbalance",
    name: "NB_Made-in-USA_Travis-Scott.pdf",
    pages: 29,
    size: "1.3 MB",
    from: "Joe Preston (NB)",
    received: "Mar 04",
  },
];

export const DC_PARSE_STEPS = [
  "Reading document structure",
  "Extracting parties & deal type",
  "Identifying financial terms",
  "Mapping rights, IP & exclusivity",
  "Flagging off-market clauses",
  "Computing composite scores",
];
